import { ApiClient } from "../ApiClient";

const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
const clientBaseUrl = process.env.REACT_APP_CLIENT_DOMAIN;

/**
 * Fetch affiliate referral codes
 * @returns {Promise<Object>} API response with list of referral codes
 */
export const fetchReferralCodes = async () => {
  const response = await fetch(`${apiBaseUrl}/referral/referral-code`, {
    method: "GET",
    credentials: "include",
  });

  if (response.ok) {
    const result = await response.json();
    const codes = result.referral_codes || result.results || [];
    
    // Transform each code to match table format
    const transformedCodes = codes.map(code => ({
      id: code.id,
      link: `${clientBaseUrl}?${code.deep_link}`,
      service: code.service === "TF" ? "Tax Filing" : code.service,
      created_at: code.created_at,
      expire_at: parseInt(code.expire_at) * 1000,
      last_seen: code.last_seen_at || null, // Keep as ISO string
      conversions: code.conversion_count || 0,
      status: Date.now() < (parseInt(code.expire_at) * 1000) ? "Active" : "Expired"
    }));
    
    return { success: true, data: transformedCodes, status: response.status };
  } else {
    return {
      success: false,
      message: "Failed to fetch referral codes",
      status: response.status
    };
  }
};

/**
 * Generate affiliate referral code
 * @param {string} service - Service name (e.g., "tax-filing")
 * @returns {Promise<Object>} API response with referral code data
 */
export const generateReferralCode = async (service) => {
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(`${apiBaseUrl}/referral/referral-code`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify({ service }),
  });

  const result = await response.json().catch(() => ({}));

  if (response.status === 201) {
    // Transform response to match table format
    const code = result.referral_code; // Access nested object
    
    // Map service code to display name
    const serviceDisplayName = code.service === "TF" ? "Tax Filing" : code.service;
    
    // Parse timestamps
    const createdAt = code.created_at; // milliseconds
    const expireAt = parseInt(code.expire_at) * 1000; // Convert seconds to milliseconds
    
    // Build full link with ? prefix
    const fullLink = `${clientBaseUrl}?${code.deep_link}`;
    
    return {
      success: true,
      created: true,
      data: {
        id: code.id,
        link: fullLink,
        service: serviceDisplayName,
        created_at: createdAt,
        expire_at: expireAt,
        last_seen: code.last_seen_at || null,
        conversions: code.conversion_count || 0,
        status: Date.now() < expireAt ? "Active" : "Expired"
      },
      status: response.status
    };
  } else if (response.status === 200) {
    // Code already exists
    return {
      success: true,
      created: false,
      message: result.message || "A valid referral code already exists for this service.",
      status: response.status
    };
  } else {
    return {
      success: false,
      message: result.message || "Failed to generate referral code",
      status: response.status
    };
  }
};

/**
 * Admin: Fetch all affiliate applications
 * @returns {Promise<Object>} API response
 */
export const fetchAffiliateApplications = async () => {
  const response = await fetch(`${apiBaseUrl}/custom-admin/affiliates-application`, {
    method: "GET",
    credentials: "include",
  });

  if (response.ok) {
    const result = await response.json();
    return { success: true, data: result, status: response.status };
  } else {
    return {
      success: false,
      message: "Failed to fetch affiliate applications",
      status: response.status,
    };
  }
};

/**
 * Admin: Accept or reject affiliate application
 * @param {number} userId - The user ID (affiliate pk)
 * @param {string} decision - "accept" or "reject"
 * @returns {Promise<Object>} API response
 */
export const decideAffiliateApplication = async (userId, decision) => {
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(`${apiBaseUrl}/custom-admin/affiliates-application/${userId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify({ decision }),
  });

  if (response.ok) {
    const result = await response.json();
    return { success: true, data: result, status: response.status };
  } else {
    const error = await response.json().catch(() => ({}));
    return {
      success: false,
      message: error.message || `Failed to ${decision} application`,
      status: response.status,
    };
  }
};

/**
 * Get affiliate details for the authenticated user
 * @returns {Promise<Object>} API response with affiliate data
 */
export const getAffiliateDetails = async () => {
  const response = await fetch(`${apiBaseUrl}/affiliates/affiliate-detail`, {
    method: "GET",
    credentials: "include",
  });

  if (response.ok) {
    const result = await response.json();
    return { success: true, data: result, status: response.status };
  } else if (response.status === 404) {
    // No affiliate found - user hasn't applied yet
    return { success: true, data: null, status: 404 };
  } else {
    const error = await response.json().catch(() => ({}));
    return {
      success: false,
      message: error.message || "Failed to fetch affiliate details",
      status: response.status,
    };
  }
};

/**
 * Submit affiliate application personal information (Create new)
 * @param {Object} data - The personal information data
 * @returns {Promise<Object>} API response
 */
export const submitAffiliatePersonalInfo = async (data) => {
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(`${apiBaseUrl}/affiliates/affiliate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (response.ok) {
    const result = await response.json();
    return { success: true, data: result, status: response.status };
  } else {
    const error = await response.json().catch(() => ({}));
    return { 
      success: false, 
      message: error.message || "Failed to submit application", 
      status: response.status,
      errors: error.errors || {}
    };
  }
};

/**
 * Update affiliate application (Edit existing - only social medias)
 * @param {Array} socialMedias - Array of social media objects
 * @returns {Promise<Object>} API response
 */
export const updateAffiliatePersonalInfo = async (socialMedias) => {
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(`${apiBaseUrl}/affiliates/affiliate-detail`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify({
      social_medias: socialMedias
    }),
  });

  if (response.ok) {
    const result = await response.json();
    return { success: true, data: result, status: response.status };
  } else {
    const error = await response.json().catch(() => ({}));
    return { 
      success: false, 
      message: error.message || "Failed to update application", 
      status: response.status,
      errors: error.errors || {}
    };
  }
};

/**
 * Update affiliate status (e.g., CCP → ACT after connected account setup)
 * @param {string} status - New status (ACT, etc.)
 * @returns {Promise<Object>} API response
 */
export const updateAffiliateStatus = async (status) => {
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(`${apiBaseUrl}/affiliates/affiliate-detail`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify({ status }),
  });

  if (response.ok) {
    const result = await response.json();
    return { success: true, data: result, status: response.status };
  } else {
    const error = await response.json().catch(() => ({}));
    return { 
      success: false, 
      message: error.message || "Failed to update affiliate status", 
      status: response.status,
    };
  }
};
